import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shell } from './components/Shell';
import { useAuth } from './lib/auth';
import { useRoute, go, useHashTarget, APP_BASE, type PublicRoute } from './lib/router';
import { api } from './lib/api';
import { SignIn } from './pages/SignIn';
import { Dashboard } from './pages/Dashboard';
import { Documents } from './pages/Documents';
import { Breakdown } from './pages/Breakdown';
import { Ask } from './pages/Ask';
import { Comms } from './pages/Comms';
import { Translate } from './pages/Translate';
// `pages/Funding` is not imported. Funding Radar is built and is not shown to
// anyone until there is a curated board of opportunities behind it; the page,
// its API routes and the curation CLI are all still here, and restoring it is
// this import, the line below, the sidebar entry and the router's section list.
import { Admin } from './pages/Admin';
import { Plan } from './pages/Plan';
import { Services } from './pages/Services';
import { MarketingShell } from './marketing/MarketingShell';
import { Home } from './marketing/Home';
import { PricingPage, DemoPage, ServicesPage } from './marketing/Pages';
import { SecurityPage } from './marketing/Security';
import { ResourcesPage } from './marketing/Resources';
import { AboutPage } from './marketing/About';
import { ContactPage } from './marketing/Contact';

/**
 * The page title, set per route.
 *
 * A single-page application has one `<title>` in its HTML and it is wrong
 * everywhere except the first page loaded. Set here so a browser tab, a
 * bookmark and a shared link say which page they are.
 */
const TITLES: Record<PublicRoute, string> = {
  home: 'InBrief | Contract intelligence for primary care',
  pricing: 'Pricing | InBrief',
  demo: 'Interactive demo | InBrief',
  services: 'InBrief Services | InBrief',
  security: 'Security and governance | InBrief',
  resources: 'Resources and FAQs | InBrief',
  about: 'About InBrief | InBrief',
  contact: 'Book a demo | InBrief',
  login: 'Log in | InBrief',
};

function usePageTitle(area: 'public' | 'app', route: PublicRoute) {
  useEffect(() => {
    document.title = area === 'app' ? 'InBrief' : TITLES[route];
  }, [area, route]);
}

/**
 * Search engines are told not to index the application.
 *
 * robots.txt already disallows /app, but a crawler that reaches a route
 * another way should still be told, and the tag travels with the page.
 */
function useIndexing(area: 'public' | 'app') {
  useEffect(() => {
    const id = 'robots-meta';
    document.getElementById(id)?.remove();
    if (area !== 'app') return;
    const meta = document.createElement('meta');
    meta.id = id;
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
  }, [area]);
}

export function App() {
  const { user, loading } = useAuth();
  const { area, route, section } = useRoute();

  usePageTitle(area, route);
  useHashTarget(area, route);
  useIndexing(area);

  // Powers the sidebar allowance meter. Skipped until signed in.
  const plan = useQuery({
    queryKey: ['plan'],
    queryFn: () => api.get<{ translations: { used: number; limit: number } }>('/api/plan'),
    enabled: Boolean(user),
  });

  // Someone already signed in who opens /login has nothing to do there.
  useEffect(() => {
    if (user && route === 'login' && area === 'public') go(APP_BASE);
  }, [user, route, area]);

  /**
   * Only the parts that need a session wait for one.
   *
   * This used to block on `loading` for every route, which meant the public
   * website — prerendered, static, and already painted — was replaced by the
   * word "Loading…" until `/api/auth/me` came back. A visitor arriving during
   * a deploy, or on a slow connection, watched the page they could already
   * read disappear; if the API was down the marketing site was down with it,
   * despite needing nothing from it.
   *
   * The public pages do not care who you are. The header offers "Log in"
   * either way.
   */
  const needsSession = area === 'app' || route === 'login';
  if (loading && needsSession) {
    return <div className="signin-wrap"><p className="field-hint">Loading…</p></div>;
  }

  if (area === 'public') {
    // The sign-in page keeps its own full-height layout rather than sitting
    // inside the marketing frame — a header offering "Book a demo" above a
    // password field is a distraction at the one moment the visitor knows
    // exactly what they came to do.
    if (route === 'login') return <SignIn />;

    return (
      <MarketingShell>
        {route === 'home' ? <Home /> : null}
        {route === 'pricing' ? <PricingPage /> : null}
        {route === 'demo' ? <DemoPage /> : null}
        {route === 'services' ? <ServicesPage /> : null}
        {route === 'security' ? <SecurityPage /> : null}
        {route === 'resources' ? <ResourcesPage /> : null}
        {route === 'about' ? <AboutPage /> : null}
        {route === 'contact' ? <ContactPage /> : null}
      </MarketingShell>
    );
  }

  // Everything below /app requires a session. Unauthenticated visitors get the
  // sign-in page rather than a redirect, so the URL they wanted is still in
  // the address bar when they arrive.
  if (!user) return <SignIn />;

  return (
    <Shell section={section} allowance={plan.data?.translations ?? null}>
      {section === 'dashboard' ? <Dashboard /> : null}
      {section === 'source' ? <Documents /> : null}
      {section === 'breakdown' ? <Breakdown /> : null}
      {section === 'ask' ? <Ask /> : null}
      {section === 'explain' ? <Comms /> : null}
      {section === 'translate' ? <Translate /> : null}
      {section === 'admin' ? <Admin /> : null}
      {section === 'plan' ? <Plan /> : null}
      {section === 'services' ? <Services /> : null}
    </Shell>
  );
}
